import React, {
  createRef, useState, useEffect, useCallback, Suspense,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { phobioSelector } from '../../redux/selectors/phoneSelectors';
import {
  addPhobioDetails,
  removePhobioDetails,
} from '../../redux/reducers/phoneReducer';
import {
  AllContentfulPhoneBrand,
  Offer,
  CombinatorSectionItem,
  ContentfulCombinatorSection,
  ContentfulTradeInFinalStep,
  ContentfulTradeInStep,
} from '../../ContentfulTypes';
import { Logo } from '../../types';
import variables from '../../styles/variables';
import { disableScrolling, enableScrolling } from '../../utils/scrollingOverride';

import Phase0 from './Phase0/Phase0';
import { TradeInPhone } from './types';
import { PhobioScreen } from './styles';

const Phase1 = React.lazy(() => import('./Phase1/Phase1'));
const Phase2 = React.lazy(() => import('./Phase2/Phase2'));
const Phase3 = React.lazy(() => import('./Phase3/Phase3'));
const Phase4 = React.lazy(() => import('./Phase4/Phase4'));
const FinalSuccess = React.lazy(() => import('./FinalSuccess/FinalSuccess'));
const FinalHelp = React.lazy(() => import('./FinalHelp/FinalHelp'));
const FinalSummary = React.lazy(() => import('./FinalSummary/FinalSummary'));

interface Props {
  phobioData: {
    tradeInStartStep: {
      combinatorSectionItems: Array<CombinatorSectionItem>;
    };
    tradeInSteps: Array<ContentfulTradeInStep>;
    tradeInFinalStep: Array<ContentfulTradeInFinalStep>;
    tradeInSummaryStep: ContentfulCombinatorSection;
    moduleImage: Logo;
    phoneBrands: AllContentfulPhoneBrand;
  };
  setActive: (isActive?: boolean) => void;
  phonePage?: boolean;
  reservationBlade?: boolean;
  tradeInPhones?: Array<{
    phoneVariants: Array<Offer>;
  }>;
  tradeInLoading?: boolean;
  mainContentId?: string;
}

const Phobio = ({
  phobioData,
  setActive = () => null,
  phonePage = false,
  reservationBlade = false,
  tradeInPhones = [],
  tradeInLoading = false,
  mainContentId,
}: Props) => {
  // Phase of phobio journey user is currently on
  const [phobioPhase, setPhobioPhase] = useState('0');

  const phobioDetails = useSelector(phobioSelector);
  const dispatch = useDispatch();

  const phaseClickHandler = useCallback((x) => {
    disableScrolling();
    setPhobioPhase(x);
    setTimeout(enableScrolling, 1000);

    // Used on the phones page to set if Phobio is active or not
    // so that MSA can be hidden
    if (x === '0' || x === 'finalSummary') {
      setActive(false);
    } else setActive(true);
  }, [setActive]);

  // If points are already set in session storage, skip to summary display
  useEffect(() => {
    if (phobioDetails?.pointsValue > 0) {
      setUserPhone(phobioDetails?.userPhone);
      setPointsValue(phobioDetails?.pointsValue);
      phaseClickHandler('finalSummary');
    }
    // TODO [2025-06-10] TMSP-3196 Re-enable lint
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [userPhone, setUserPhone] = useState<TradeInPhone | undefined>(undefined);

  // yes no answers to remaining steps
  const [phase2answer, setPhase2answer] = useState<string | null>(null);
  const [phase3answer, setPhase3answer] = useState<string | null>(null);
  const [phase4answer, setPhase4answer] = useState<string | null>(null);
  const [pointsValue, setPointsValue] = useState<number | undefined>();

  // Put status of captcha checkbox in state and set as complete
  const [isCaptchaChecked, setIsCaptchaChecked] = useState(false);
  const [hasErrorCaptcha, setHasErrorCaptcha] = useState(false);
  const recaptchaRef = createRef();
  const captchaHandler = (status) => {
    setIsCaptchaChecked(status);
    if (status === true) {
      setHasErrorCaptcha(false);
    } else if (status === false) {
      setHasErrorCaptcha(true);
    }
  };

  const getPoints = useCallback((phase1response, phase2response, phase3response) => {
    if (phase1response === 'no') {
      return userPhone?.tradeInValue.filter(
        ({ condition }) => condition === 'dead',
      );
    }
    if (phase2response === 'yes' || phase3response === 'yes') {
      return userPhone?.tradeInValue.filter(
        ({ condition }) => condition === 'damaged',
      );
    }
    return userPhone?.tradeInValue.filter(
      ({ condition }) => condition === 'working',
    );
  }, [userPhone?.tradeInValue]);

  // Points value response from API
  const finalStepResponseHandler = useCallback(() => {
    // TODO: [2025-06-10] TMSP-2993 Improve test coverage of this section
    if (phase4answer) {
      const tradeInValue = getPoints(phase2answer, phase3answer, phase4answer);

      dispatch(
        addPhobioDetails({
          userPhone,
          pointsValue: tradeInValue?.[0].points,
        }),
      );
      setPointsValue(tradeInValue?.[0].points);
      disableScrolling();
      setPhobioPhase(reservationBlade ? 'finalSummary' : 'finalSuccess');
      setTimeout(enableScrolling, 1000);
    }
  }, [dispatch, getPoints, phase2answer, phase3answer, phase4answer, reservationBlade, userPhone]);

  const resetPhobio = () => {
    disableScrolling();
    phaseClickHandler('1');
    setTimeout(enableScrolling, 1000);
    setUserPhone(undefined);
    setPhase2answer(null);
    setPhase3answer(null);
    setPhase4answer(null);
    setPointsValue(undefined);
    setIsCaptchaChecked(false);
    dispatch(removePhobioDetails());
  };

  return (
    <PhobioScreen
      $backgroundColor={variables.colorWhite}
    >
      {/* Phase 0 - Initial phase */}
      {phobioPhase === '0' && (
        <Phase0
          phaseClickHandler={phaseClickHandler}
          phobioData={phobioData.tradeInStartStep}
          phonePage={phonePage}
          reservationBlade={reservationBlade}
          mainContentId={mainContentId}
        />
      )}

      <Suspense>
        {/* Phase 1 */}
        {phobioPhase === '1' && (
          <Phase1
            phobioData={phobioData}
            phaseClickHandler={phaseClickHandler}
            userPhone={userPhone}
            setUserPhone={setUserPhone}
            tradeInPhones={tradeInPhones}
            tradeInLoading={tradeInLoading}
          />
        )}

        {/* Phase 2 */}
        {phobioPhase === '2' && (
          <Phase2
            phobioData={phobioData}
            phaseClickHandler={phaseClickHandler}
            userPhone={userPhone}
            phase2answer={phase2answer}
            setPhase2answer={setPhase2answer}
          />
        )}

        {/* Phase  3 */}
        {phobioPhase === '3' && (
          <Phase3
            phobioData={phobioData}
            phaseClickHandler={phaseClickHandler}
            userPhone={userPhone}
            phase3answer={phase3answer}
            setPhase3answer={setPhase3answer}
          />
        )}

        {/* Phase  4 */}
        {phobioPhase === '4' && (
          <Phase4
            phobioData={phobioData}
            phaseClickHandler={phaseClickHandler}
            userPhone={userPhone}
            phase2answer={phase2answer}
            phase3answer={phase3answer}
            phase4answer={phase4answer}
            setPhase4answer={setPhase4answer}
            isCaptchaChecked={isCaptchaChecked}
            captchaHandler={captchaHandler}
            recaptchaRef={recaptchaRef}
            setIsCaptchaChecked={setIsCaptchaChecked}
            hasErrorCaptcha={hasErrorCaptcha}
            finalStepResponseHandler={finalStepResponseHandler}
          />
        )}

        {/* Final Success Screen - Points value */}
        {phobioPhase === 'finalSuccess' && (
          <FinalSuccess
            phobioData={phobioData}
            phaseClickHandler={phaseClickHandler}
            pointsValue={pointsValue}
            userPhone={userPhone}
            resetPhobioHandler={resetPhobio}
          />
        )}

        {/* Final Help Screen - More info needed */}
        {phobioPhase === 'finalHelp' && (
          <FinalHelp
            phobioData={phobioData}
            phaseClickHandler={phaseClickHandler}
            resetPhobioHandler={resetPhobio}
          />
        )}

        {/* Final Summary Screen - Points recap */}
        {phobioPhase === 'finalSummary' && phobioDetails && (
          <FinalSummary
            phobioData={phobioData}
            resetPhobioHandler={resetPhobio}
            phobioDetails={phobioDetails}
            phonePage={phonePage || reservationBlade}
          />
        )}
      </Suspense>
      
    </PhobioScreen>
  );
};

export default Phobio;

// ===============
// TO DOS
// ===============
// Why does tab focus return to start of phone model row after choosing a phone?
// We will possibly be adding a ticket that allows the user to switch pages part way through the flow, and any other place the user ends up that has Phobio, will show step 0. But if they click "get started" it will open up to the next step they have not yet completed (IE it remembers the answers so far and keeps them in storage)
// The same logic happens when user clicks the "x" button. Collapses to step 0, but "get started" reinstates at the next unanswered step.
// NOTE FOR BRAD: <select> console error can be ignored. I did change it based on the reco and the warning went away, but it rerenders the <option>s and they layer in the array. So I put it back.
// captcha testing key in env file stopped working on local build. Hard coded it. Figure out the issue and switch back to env soon.
// BRAD make a README for Phobio!
// Final success step, desktop images between 1200 and 767 need sizing

// ===============
// FOR TESTING
// ===============
// 356167090293132 returns multiple iPhones
// 354819093604010 returns 1 android phone
// 540985389671040 returns NO_MATCH_FOUND
// leaving IMEI blank returns IMEI_INVALID
// leaving carrier blank returns INVALID_CARRIER
// also can get TIMEOUT or UNKNOWN_ERROR
